import React, { useEffect, useRef, useState } from "react";
import { IAgent } from "../../../../models/Agent.model";
import "./DragDnd.scss";
import { FaUserAlt } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import SharedFlow from "../ReactFlow/ReactFlow";
import { INode } from "../ReactFlow/models/node.model";
import { IEdge } from "../ReactFlow/models/edge.model";

export default function DragDnd({
  dragSection,
  targetSection,
  handleDragChange,
}: {
  dragSection: IAgent[];
  targetSection: IAgent[];
  handleDragChange: any;
}) {
  const [sourceItems, setSourceItems] = useState<IAgent[]>(
    dragSection.filter((agent) => {
      return !targetSection.some((item) => item.name === agent.name);
    })
  );

  const [targetItems, setTargetItems] = useState<IAgent[]>(targetSection);
  const [activeIndex, setActiveIndex] = useState<number | null>();
  const [renderedWorkflows, setRenderedWorkflows] = useState<{
    nodes: INode[];
    edges: IEdge[];
  }>({ nodes: [], edges: [] });

  const prevTargetItemsRef = useRef<IAgent[]>([]);

  // Handler for starting drag, setting the item id as data
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    e.dataTransfer.setData("text/plain", id);
  };

  // Handler for dropping an item from the source to the target
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const id = e.dataTransfer.getData("text/plain");
    const draggedItem = sourceItems.find((item) => item.id === id);

    if (draggedItem) {
      // Prevent adding duplicate items in the target list
      if (!targetItems.find((item) => item.id === draggedItem.id)) {
        setTargetItems([...targetItems, draggedItem]);
        setSourceItems(sourceItems.filter((item) => item.id !== id));
      }
    }
    e.preventDefault();
  };

  // Prevent default behavior for dragOver
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // Handler for rearranging items within the target list
  const handleReorder = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    const id = e.dataTransfer.getData("text/plain");
    const draggedItemIndex = targetItems.findIndex((item) => item.id === id);

    if (draggedItemIndex !== -1 && draggedItemIndex !== index) {
      // Reorder items in the target list
      const updatedItems = [...targetItems];
      const [draggedItem] = updatedItems.splice(draggedItemIndex, 1);
      updatedItems.splice(index, 0, draggedItem);
      setTargetItems(updatedItems);
    }
    e.preventDefault();
  };

  const handleTargetFlow = (targetedAgents: IAgent[]) => {
    const createNodes: INode[] = [
      {
        id: "1",
        data: { label: "__start__" },
        position: { x: 260, y: 0 },
        className: "",
      },
    ];
    targetedAgents.forEach((item, i) => {
      const index = i + 2;
      createNodes.push({
        id: index.toString(),
        data: { label: item.name },
        position: { x: 260, y: 50 + i * 50 },
        className: "",
      });
    });
    createNodes.push({
      id: (targetedAgents.length + 2).toString(),
      data: { label: "__end__" },
      position: { x: 260, y: 0 + (targetedAgents.length + 1) * 50 },
      className: "",
    });
    const createEdges: IEdge[] = createNodes.map((_, i) => {
      const source = (i + 1).toString();
      const target = (i + 2).toString();
      return {
        id: `e${source}-${target}`,
        source,
        target,
        animated: true,
      };
    });

    setRenderedWorkflows({
      nodes: createNodes,
      edges: createEdges,
    });
  };

  useEffect(() => {
    // Check if targetItems has changed
    const prevTargetItems = prevTargetItemsRef.current;

    const hasChanged =
      prevTargetItems.length !== targetItems.length ||
      prevTargetItems.some((item, index) => item.id !== targetItems[index]?.id);

    if (hasChanged) {
      handleTargetFlow(targetItems);
      handleDragChange(targetItems);
      prevTargetItemsRef.current = targetItems; // Update the previous items reference
    }
  }, [handleDragChange, targetItems]);

  return (
    <div className="drag-dnd">
      {/* Source List */}
      <div className="source-list">
        <h3>Agent List</h3>
        {sourceItems
          .reduce((list: any[], item) => {
            if (list.length > 0) {
              if (
                list.some((listItem) => listItem.category === item.category)
              ) {
                list = list.map((listItem) => {
                  if (listItem.category === item.category) {
                    listItem.categoryList.push(item);
                  }
                  return listItem;
                });
              } else {
                list.push({
                  category: item.category,
                  categoryList: [item],
                });
              }
            } else {
              list.push({
                category: item.category,
                categoryList: [item],
              });
            }
            return list;
          }, [])
          .map((item, i) => (
            <div className="section-group-item" key={i}>
              <label
                onClick={() => setActiveIndex(activeIndex === i ? null : i)}
              >
                {item.category}{" "}
                {i === activeIndex ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </label>
              {activeIndex === i &&
                item.categoryList.map((listItem: IAgent, j: number) => {
                  return (
                    <div
                      className="section-item"
                      key={j + "" + listItem.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, listItem.id)}
                    >
                      <FaUserAlt />
                      <p>{listItem.name}</p>
                    </div>
                  );
                })}
            </div>
          ))}
      </div>

      {/* Target List */}
      <div
        className="target-list"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <h3>Mapped Agent List</h3>
        {targetItems.length === 0 && <p>Drop items here</p>}
        {targetItems.map((item, index) => (
          <div
            className="section-item"
            key={item.id}
            draggable
            onDragStart={(e) => handleDragStart(e, item.id)}
            onDrop={(e) => handleReorder(e, index)}
            onDragOver={handleDragOver}
          >
            <FaUserAlt />
            <p>{item.name}</p>
          </div>
        ))}
      </div>
      <div className="target-flow">
        <SharedFlow nodeProps={renderedWorkflows} maxZoom={1.4} />
      </div>
    </div>
  );
}
